import * as Sentry from '@sentry/nextjs'
import { User, browserSessionPersistence, setPersistence } from 'firebase/auth'
import React, { createContext, useEffect, useState } from 'react'

import { auth } from '@/lib/firebase/firebaseConfig'

type AuthState =
  | {
      user: User | null
      isLoading: false
    }
  | {
      user: undefined
      isLoading: true
    }

interface IAuthContext {
  authState: AuthState
}

export const AuthContext = createContext<IAuthContext>({
  authState: { isLoading: true, user: undefined },
})

interface AuthContextProviderProps {
  children: React.ReactNode
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: true,
    user: undefined,
  })

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setAuthState({
        isLoading: false,
        user,
      })
    })

    // Set local persistence for Apple user agents only
    const setPersistenceForAppleUserAgents = async () => {
      try {
        // Check if user agent indicates Apple device/browser
        if (typeof window !== 'undefined') {
          const userAgent = window.navigator?.userAgent || ''
          const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent)
          const isMacOSDesktop = /Macintosh/.test(userAgent) && 'ontouchend' in document
          const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS|Edge|Edg|OPR|Opera|Vivaldi/.test(userAgent)
          const isAppleUserAgent = (isIOSDevice || isMacOSDesktop) && isSafari

          if (isAppleUserAgent) {
            await setPersistence(auth, browserSessionPersistence)
            console.log('Firebase auth persistence set to browserSessionPersistence for Apple user agent')
          }
        }
        // For non-Apple user agents, use default persistence (no-op)
      } catch (error) {
        Sentry.captureException('Failed to set local persistence for Apple user agent', {
          extra: { error },
        })
      }
    }

    setPersistenceForAppleUserAgents()

    return () => unsubscribe()
  }, [])

  return <AuthContext.Provider value={{ authState }}>{children}</AuthContext.Provider>
}
